/**
 * The re-order rule (z8nrz7f7dz), for the live calculator.
 *
 * Mirrors cgt-backend src/inventory/reorder/reorder.ts, which is what decides
 * the state and suggested quantity the Re-Order tab lists — keep the two in
 * sync, or the calculator will disagree with the list beside it. Pure and
 * dependency-free on purpose: vitest is node-only, so this is where the rule is
 * tested.
 *
 * One difference by design: with no daily consumption set, the server falls
 * back to 90 days of consumption history. The calculator has no history, so it
 * uses exactly what was entered. As on the server, a zero rate, threshold or
 * quantity means "not set", and derived figures are rounded to two decimals
 * before they are compared.
 */

export type ReorderState = 'out' | 'reorder' | 'ok'

/** Where a reorder point came from, in order of preference. */
export type ReorderPointSource = 'calculated' | 'manual' | 'min_stock'

/** A material's re-order parameters — its inventory_unit values. Null means not set. */
export interface ReorderParams {
  dailyConsumption: number | null
  orderLeadTimeDays: number | null
  releaseTimeDays: number | null
  safetyStockDays: number | null
  /** The hand-set reorder point, used when it cannot be calculated. */
  reorderPoint: number | null
  minStock: number | null
  maxStock: number | null
  eoq: number | null
}

export interface ReorderStock {
  onHand: number
  available: number
  onOrder: number
}

export interface ReorderResult {
  totalLeadTimeDays: number | null
  safetyStockUnits: number | null
  reorderPoint: number | null
  reorderPointSource: ReorderPointSource | null
  state: ReorderState
  suggestedQty: number
}

/** The inventory_unit field keys the rule reads. */
export const REORDER_PARAM_KEYS = [
  'dailyConsumption',
  'orderLeadTimeDays',
  'releaseTimeDays',
  'safetyStockDays',
  'reorderPoint',
  'minStock',
  'maxStock',
  'eoq',
] as const satisfies readonly (keyof ReorderParams)[]

/**
 * A stored or typed value as a number, or null when it is not one. A number
 * input hands back a string, and an empty one is "not set", never zero.
 */
export function toReorderNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }
  return null
}

/** Read the parameters through `get`, so a stored attribute map and a form being edited share one path. */
export function reorderParamsFrom(get: (key: keyof ReorderParams) => unknown): ReorderParams {
  return Object.fromEntries(
    REORDER_PARAM_KEYS.map((k) => [k, toReorderNumber(get(k))]),
  ) as unknown as ReorderParams
}

/** Derived figures are rounded before they are compared, so the state matches what is shown. */
function round2(n: number): number {
  return Math.round(n * 100) / 100
}

/** A day count: zero allowed, negative reads as not set. */
function days(n: number | null): number | null {
  return n !== null && n >= 0 ? n : null
}

/** A rate, threshold or quantity: zero means not set. */
function positive(n: number | null): number | null {
  return n !== null && n > 0 ? n : null
}

export function computeReorder(p: ReorderParams, stock: ReorderStock): ReorderResult {
  const d = positive(p.dailyConsumption)
  const lead = days(p.orderLeadTimeDays)
  const release = days(p.releaseTimeDays)
  const safety = days(p.safetyStockDays)

  const totalLeadTimeDays = lead !== null ? lead + (release ?? 0) : null
  const safetyStockUnits = d !== null ? round2(d * (safety ?? 0)) : null

  let reorderPoint: number | null = null
  let reorderPointSource: ReorderPointSource | null = null
  if (d !== null && totalLeadTimeDays !== null) {
    reorderPoint = round2(d * (totalLeadTimeDays + (safety ?? 0)))
    reorderPointSource = 'calculated'
  } else if (positive(p.reorderPoint) !== null) {
    reorderPoint = p.reorderPoint
    reorderPointSource = 'manual'
  } else if (positive(p.minStock) !== null) {
    reorderPoint = p.minStock
    reorderPointSource = 'min_stock'
  }

  const position = stock.available + stock.onOrder
  const state: ReorderState =
    stock.onHand <= 0 ? 'out' : reorderPoint !== null && position <= reorderPoint ? 'reorder' : 'ok'

  return {
    totalLeadTimeDays,
    safetyStockUnits,
    reorderPoint,
    reorderPointSource,
    state,
    suggestedQty: state === 'ok' ? 0 : suggestOrderQty(p.maxStock, p.eoq, reorderPoint, position),
  }
}

/**
 * How much a due material should order, against its inventory position
 * (available + on order) so stock already on order is never ordered twice.
 * Order-up-to-max when a maximum is set (min/max); otherwise whole EOQ
 * batches, as many as lift the position above the ROP (s, nQ); otherwise just
 * enough to lift it above the ROP, so the material stops being due once the
 * order is placed. Whole units, never negative. Mirrors cgt-backend's
 * src/inventory/reorder/reorder.ts.
 */
function suggestOrderQty(
  maxStockRaw: number | null,
  eoqRaw: number | null,
  reorderPoint: number | null,
  position: number,
): number {
  const maxStock = positive(maxStockRaw)
  const eoq = positive(eoqRaw)
  if (maxStock !== null) return Math.max(0, Math.ceil(round2(maxStock - position)))
  if (reorderPoint === null) return eoq !== null ? Math.ceil(eoq) : 0
  const shortfall = round2(reorderPoint - position)
  if (shortfall < 0) return 0
  if (eoq !== null) return Math.ceil(round2((Math.floor(shortfall / eoq) + 1) * eoq))
  return Math.floor(shortfall) + 1
}

/**
 * How many days the current stock lasts at the daily consumption, to one
 * decimal. Null without a positive rate: nothing is being used, so the stock
 * does not run out.
 */
export function daysOfCover(currentStock: number, dailyConsumption: number | null): number | null {
  const d = positive(dailyConsumption)
  if (d === null) return null
  return Math.round((Math.max(0, currentStock) / d) * 10) / 10
}

/** When the inventory position reaches the reorder point, relative to today. */
export interface ReorderTiming {
  /** The day the order should be placed (today when it is already due). */
  date: Date
  /**
   * Whole days from today: positive = that many days left, 0 = today,
   * negative = overdue by that many days.
   */
  daysFromToday: number
}

/**
 * When to place the next order: the day the inventory position (available +
 * on order) falls to the reorder point at the daily consumption. Already at
 * or below it, the order is due today, or overdue by the days of consumption
 * the position is short. Null without a rate or a reorder point.
 */
export function reorderTiming(
  stock: Pick<ReorderStock, 'available' | 'onOrder'>,
  reorderPoint: number | null,
  dailyConsumption: number | null,
  today: Date,
): ReorderTiming | null {
  const d = positive(dailyConsumption)
  if (d === null || reorderPoint === null) return null
  const gap = round2(stock.available + stock.onOrder - reorderPoint)
  const daysFromToday = gap > 0 ? Math.floor(gap / d) : -Math.floor(-gap / d)
  const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() + Math.max(0, daysFromToday))
  return { date, daysFromToday: daysFromToday === 0 ? 0 : daysFromToday }
}

/**
 * The reorder point worked through with the live figures, e.g.
 * "Reorder point = 100 × (7 + 2 + 30) = 3,900 units". Null when there is none.
 */
export function reorderPointFormula(p: ReorderParams, result: Pick<ReorderResult, 'reorderPoint' | 'reorderPointSource'>): string | null {
  const rop = formatReorderNumber(result.reorderPoint)
  switch (result.reorderPointSource) {
    case 'calculated': {
      const n = (v: number | null) => formatReorderNumber(days(v) ?? 0)
      return `Reorder point = ${formatReorderNumber(p.dailyConsumption)} × (${n(p.orderLeadTimeDays)} + ${n(p.releaseTimeDays)} + ${n(p.safetyStockDays)}) = ${rop} units`
    }
    case 'manual':
      return `Reorder point = ${rop} units (set by hand)`
    case 'min_stock':
      return `Reorder point = ${rop} units (minimum stock)`
    default:
      return null
  }
}

/** A figure for display: grouped, at most two decimals, a dash when not set. */
export function formatReorderNumber(n: number | null | undefined): string {
  return n == null ? '—' : n.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

/** The PO button's tooltip, shared by the materials table and the Re-Order tab. */
export function reorderActionTitle(state: ReorderState, suggestedQty: number): string {
  return state === 'ok'
    ? 'Stock sufficient'
    : `Re-order: suggested ${formatReorderNumber(suggestedQty)} units`
}

/** The decision as the screens word it. */
export const REORDER_STATE_LABEL: Record<ReorderState, string> = {
  out: 'Out of Stock',
  reorder: 'Re-order',
  ok: 'Stock Sufficient',
}

export const REORDER_STATE_STYLE: Record<ReorderState, string> = {
  out: 'bg-red-100 text-red-700',
  reorder: 'bg-amber-100 text-amber-800',
  ok: 'bg-green-100 text-green-700',
}

export const REORDER_POINT_SOURCE_LABEL: Record<ReorderPointSource, string> = {
  calculated: 'Calculated',
  manual: 'Manual',
  min_stock: 'Min stock',
}
